import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Building2, Check } from "lucide-react";

export default function Onboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState(user?.seller_type || "private");
  const [business, setBusiness] = useState(user?.business_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/seller/onboard", { seller_type: type, business_name: business || null, phone: phone || null });
      await refreshUser();
      toast.success("Profile set up!");
      navigate("/pricing");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const Opt = ({ val, icon: Icon, title, desc }) => (
    <button type="button" data-testid={`seller-type-${val}`} onClick={() => setType(val)}
      className={`text-left p-5 rounded-xl border-2 transition-all ${type === val ? "border-accent bg-accent/5" : "border-border hover:border-slate-300"}`}>
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-lg bg-primary/5 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
        {type === val && <Check className="w-5 h-5 text-accent" />}
      </div>
      <h3 className="font-heading font-semibold mt-3">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{desc}</p>
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading text-3xl font-bold">How will you be selling?</h1>
      <p className="text-muted-foreground mt-2">Choose the account type that fits you. You can change this later.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Opt val="private" icon={User} title="Private seller" desc="Selling your own car. List free for your first 3 months, then £9.99 a month." />
        <Opt val="dealer" icon={Building2} title="Dealer" desc="Running a forecourt. One flat monthly price per dealership, with unlimited cars." />
      </div>
      {type === "dealer" && (
        <div className="mt-6 space-y-4">
          <div><Label>Business name</Label><Input data-testid="onboard-business" value={business} onChange={(e) => setBusiness(e.target.value)} className="mt-1" placeholder="e.g. Oscars Motors Ltd" /></div>
          <div><Label>Contact phone</Label><Input data-testid="onboard-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" placeholder="Optional" /></div>
        </div>
      )}
      <Button onClick={submit} disabled={loading} className="mt-8 h-12 px-8 bg-accent hover:bg-emerald-600 text-white" data-testid="onboard-continue">Continue to plans</Button>
    </div>
  );
}
